import { MigrationType } from "./Statistics/MigrationType";
import { SourceLinkOrigin, SourceLinkReason } from "./SourceLink";
import { AppColorVariant } from "../theme/AppColorVariant";

/**
 * What a cycle concluded about a configured source root. Its own two values rather than `SourceLinkOutcome`,
 * whose vocabulary is about files: a root yields migration entities.
 */
export enum SourceRootOutcome {
  Enumerated = "enumerated",
  Unreachable = "unreachable",
}

export const SourceRootOutcomeLabels: Record<SourceRootOutcome, string> = {
  [SourceRootOutcome.Enumerated]: "Readable",
  [SourceRootOutcome.Unreachable]: "Unreachable",
};

export const SourceRootOutcomeColors: Record<SourceRootOutcome, AppColorVariant> = {
  [SourceRootOutcome.Enumerated]: "success",
  [SourceRootOutcome.Unreachable]: "danger",
};

/**
 * A path discovery enumerates migration entities out of, and what the last cycle to report it concluded.
 *
 * One row per root per migration type, overwritten by every report — so `discoveryCycleId` is the only
 * attribution there is, and a root that has since been read again keeps no trace of the cycle that found it
 * broken. The API serialises with `JsonIgnoreCondition.WhenWritingNull`, so nullable fields arrive absent.
 */
export interface ConfiguredSourceRoot {
  id: string;
  /** The path as the source spells it now, refreshed by every report. */
  pathRoot: string;
  migrationType: MigrationType;
  origin: SourceLinkOrigin;
  outcome: SourceRootOutcome;
  /** Absent when the root could be enumerated. */
  reason?: SourceLinkReason;
  /** Entities that came out of this root on the cycle named by `discoveryCycleId`, not a running total. */
  entitiesEnumerated: number;
  /**
   * Links beneath the root passed over because it was unreachable. Links under an outage are left alone rather
   * than failed one by one, so this is the only figure that says what the outage cost.
   */
  linksSkipped: number;
  lastSeenOn: string;
  /** Absent while the outage stands. */
  resolvedOn?: string;
  /** The cycle that last reported this root. Absent from a client that reported roots outside a cycle. */
  discoveryCycleId?: string;
}

export const isUnreachable = (root: ConfiguredSourceRoot): boolean =>
  root.outcome === SourceRootOutcome.Unreachable;
